import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { Factory } from "lucide-react";
import { SERVICES, getServiceBySlug } from "@/data/services";

function HeroMedia({ s }) {
  if (s.img) {
    return (
      <img
        src={s.img}
        alt={s.title}
        className="w-full h-full object-cover"
      />
    );
  }
  const Icon = s.icon || Factory;
  return (
    <div
      className="w-full h-full flex items-center justify-center relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #0E2A57 0%, #14366B 50%, #0E2A57 100%)",
      }}
    >
      <Icon size={180} strokeWidth={1.1} className="text-[#BEE7F5]" />
    </div>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const currentIndex = SERVICES.findIndex((s) => s.slug === slug);
  const next = SERVICES[(currentIndex + 1) % SERVICES.length];
  const prev = SERVICES[(currentIndex - 1 + SERVICES.length) % SERVICES.length];

  return (
    <div data-testid={`service-detail-${slug}`} className="bg-white text-[#050B14]">
      <Navigation />

      <main>
        {/* Hero */}
        <section className="relative bg-[#F4F8FC] pt-32 md:pt-40 pb-16 md:pb-24 border-b border-[#E5EBF2]">
          <div className="container-ags">
            <button
              onClick={() => navigate(-1)}
              data-testid="service-back-button"
              className="group inline-flex items-center gap-2 text-sm font-medium text-[#0E2A57] hover:text-[#2BB6E5] transition-colors mb-10"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              All Services
            </button>

            <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-end">
              <div className="md:col-span-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="h-px w-16 bg-[#2BB6E5]" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                    {service.tag}
                  </span>
                </div>
                <h1 className="font-heading font-extrabold text-4xl md:text-6xl lg:text-[5rem] leading-[0.98] tracking-[-0.04em] text-[#050B14]">
                  {service.title}
                </h1>
                <p className="mt-8 text-[#475569] text-lg md:text-xl leading-relaxed max-w-2xl">
                  {service.body}
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="aspect-[4/3] bg-white border border-[#E5EBF2] overflow-hidden">
                  <HeroMedia s={service} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Intro + Features */}
        <section className="bg-white py-24 md:py-36">
          <div className="container-ags grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#0E2A57]/60 font-medium">
                Overview
              </span>
              <h2 className="mt-6 font-heading font-extrabold text-3xl md:text-5xl leading-[1.05] tracking-[-0.035em] text-[#050B14]">
                What this service{" "}
                <span className="italic-display font-normal text-[#0E2A57]">
                  includes
                </span>
                .
              </h2>
            </div>
            <div className="md:col-span-7 space-y-6">
              <p className="text-[#475569] text-lg leading-relaxed">{service.intro}</p>

              <div className="flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-2 bg-[#0E2A57] text-white px-3 py-1.5 font-semibold tracking-wide">
                  <span className="inline-block w-1.5 h-1.5 bg-[#2BB6E5] rounded-full" />
                  Site Foreman on every shift
                </span>
                <span className="inline-flex items-center gap-2 bg-[#F4F8FC] text-[#0E2A57] border border-[#0E2A57]/15 px-3 py-1.5 font-semibold tracking-wide">
                  <span className="inline-block w-1.5 h-1.5 bg-[#0E2A57] rounded-full" />
                  Service Supervisor per contract
                </span>
                <span className="inline-flex items-center gap-2 bg-[#F4F8FC] text-[#0E2A57] border border-[#0E2A57]/15 px-3 py-1.5 font-semibold tracking-wide">
                  <span className="inline-block w-1.5 h-1.5 bg-[#0E2A57] rounded-full" />
                  Monthly Manager audit
                </span>
              </div>

              <ul className="mt-10 divide-y divide-[#E5EBF2] border-t border-b border-[#E5EBF2]">
                {service.features.map((f, i) => (
                  <li
                    key={f}
                    data-testid={`service-feature-${i}`}
                    className="py-5 flex items-start gap-4"
                  >
                    <span className="bg-[#0E2A57] text-white w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check size={14} strokeWidth={3} />
                    </span>
                    <span className="text-[#050B14] text-base md:text-lg leading-relaxed">
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to={`/quote/${service.slug}`}
                data-testid="service-cta-quote"
                className="mt-10 group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#081A37] px-10 py-5 font-semibold tracking-wide transition-colors"
              >
                Request a Quote for {service.title.replace(" Cleaning", "")}
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>
          </div>
        </section>

        {service.sections && service.sections.length > 0 && (
          <section
            data-testid="service-rich-sections"
            className="bg-[#F4F8FC] py-24 md:py-36 border-t border-[#E5EBF2]"
          >
            <div className="container-ags space-y-16 md:space-y-20">
              {service.sections.map((sec, sIdx) => (
                <div
                  key={sec.heading}
                  data-testid={`service-rich-${sIdx}`}
                  className="grid md:grid-cols-12 gap-10 md:gap-16"
                >
                  <div className="md:col-span-5">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="h-px w-16 bg-[#2BB6E5]" />
                      <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                        0{sIdx + 1} / {service.sections.length}
                      </span>
                    </div>
                    <h2 className="font-heading font-extrabold text-3xl md:text-4xl leading-[1.05] tracking-[-0.035em] text-[#0E2A57]">
                      {sec.heading}
                    </h2>
                    {sec.body && (
                      <p className="mt-6 text-[#475569] text-base md:text-lg leading-relaxed">
                        {sec.body}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-7 grid gap-px bg-[#E5EBF2] border border-[#E5EBF2] self-start">
                    {sec.items.map((it, iIdx) => (
                      <div
                        key={it.k}
                        data-testid={`service-rich-${sIdx}-item-${iIdx}`}
                        className="bg-white p-5 md:p-6 flex items-start gap-5"
                      >
                        <span className="bg-[#0E2A57] text-[#BEE7F5] w-10 h-10 flex items-center justify-center flex-shrink-0 font-heading font-black text-sm">
                          {String(iIdx + 1).padStart(2, "0")}
                        </span>
                        <div className="flex-1">
                          <div className="font-heading font-bold text-base md:text-lg text-[#0E2A57] tracking-tight">
                            {it.k}
                          </div>
                          <p className="mt-2 text-[#475569] text-sm md:text-base leading-relaxed">
                            {it.v}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Prev/Next */}
        <section className="bg-[#F4F8FC] py-16 md:py-20 border-y border-[#E5EBF2]">
          <div className="container-ags grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Link
              to={`/services/${prev.slug}`}
              data-testid="service-nav-prev"
              className="group bg-white border border-[#E5EBF2] hover:border-[#0E2A57]/40 hover:shadow-xl transition-all p-6 md:p-8 flex items-center gap-5"
            >
              <ArrowLeft
                size={22}
                className="text-[#0E2A57] flex-shrink-0 transition-transform group-hover:-translate-x-1"
              />
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                  Previous
                </div>
                <div className="mt-1 font-heading font-bold text-lg text-[#0E2A57] truncate">
                  {prev.title}
                </div>
              </div>
            </Link>
            <Link
              to={`/services/${next.slug}`}
              data-testid="service-nav-next"
              className="group bg-white border border-[#E5EBF2] hover:border-[#0E2A57]/40 hover:shadow-xl transition-all p-6 md:p-8 flex items-center gap-5 md:flex-row-reverse md:text-right"
            >
              <ArrowRight
                size={22}
                className="text-[#0E2A57] flex-shrink-0 transition-transform group-hover:translate-x-1"
              />
              <div className="flex-1">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                  Next
                </div>
                <div className="mt-1 font-heading font-bold text-lg text-[#0E2A57] truncate">
                  {next.title}
                </div>
              </div>
            </Link>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
